export const PROPOSAL = {
    LOADING_MESSAGE: 'Loading proposal details...',
    BACK_TO_ORDERS: 'Back to Orders',
    ACADEMIC_YEAR_LABEL: 'Academic Year:',
    PRODUCTS_LABEL: 'Products',
    CURRENCY_SYMBOL: '₹',
    ORDER_ID_TOOLTIP: 'Order Id',
    ORDER_ID_LABEL: 'Order ID',
    SCHOOL_CODE_LABEL: 'School Code',
    PROGRAM_DURATION_LABEL: 'Program duration',
    ACADEMIC_YEAR_LABEL_HEADER: 'Academic Year',
} as const

export const NO_DATA = {
    ICON: 'folder_open',
    TITLE: 'No Order Details Available',
    MESSAGE:
        'There are no order details available at the moment. Please check back later or contact support if you believe this is an error.',
    BACK_BUTTON_TEXT: 'Back to Orders',
} as const

export const INSTALLMENTS = {
    LOADING_MESSAGE: 'Loading payment terms...',
    DUE_DATE_PREFIX: 'Due: ',
    INSTALLMENT_PREFIX: 'Installment ',
    INSTALLMENT_SINGULAR: 'installment',
    INSTALLMENT_PLURAL: 'installments',
    TOTAL_AMOUNT_SUFFIX: 'of total',
    STATUS_PENDING: 'pending',
    STATUS_PAID: 'paid',
    STATUS_PENDING_DISPLAY: 'Pending',
    STATUS_PAID_DISPLAY: 'Paid',
    UNKNOWN_STATUS: '-',
    DUE_NOW_BADGE: 'Due now',
    TOTAL_CONTRACT_VALUE_LABEL: 'Total contract value',
    INSTALLMENT_DATE_LOCKED_HINT: 'Set the previous instalment due date first to edit this date.',
    REVIEW_SUBTITLE:
        "Review the installment schedule below. You can edit the dates to suit your school's payment cycle.",
    ALLOCATION_LABEL: 'allocated',
    PERCENT_TOTAL_ERROR: 'Installment percentages must total 100%. Currently',
} as const

export const INSTALLMENT_SUMMARY_FORMAT = {
    EQUAL: 'equal',
    UNEQUAL: 'unequal',
} as const

export type InstallmentSummaryFormat =
    (typeof INSTALLMENT_SUMMARY_FORMAT)[keyof typeof INSTALLMENT_SUMMARY_FORMAT]

export const ROUTE_PATHS = {
    ORDERS: '/school-site/orders',
    VIEW_PROPOSAL: '/school-site/view-proposal',
    VIEW_CONTRACT: 'view-contract',
    SIGN_CONTRACT: 'sign-contract',
} as const

// Point of Contact Component Constants
export const POINT_OF_CONTACT = {
    LOADING_MESSAGE: 'Loading contact information...',
    NO_PRIMARY_CONTACT: 'No primary contact available',
    NO_OTHER_CONTACTS: 'No other secondary contacts available',
    EMPTY_PRIMARY_ICON: 'person_off',
    EMPTY_LIST_ICON: 'people_outline',
    PRIMARY_CONTACT_BADGE: 'Primary Contact',
    PRIMARY_BADGE: 'Primary',
    TABLE_HEADERS: {
        NAME: 'Name',
        ROLE: 'Role',
        EMAIL: 'Email',
        PHONE: 'Phone',
    },
    BREAKPOINTS: {
        MOBILE: 768,
        TABLET: 1024,
    },
    CARD: {
        MIN_WIDTH: 280,
        GAP: 24,
        TABLET_WIDTH: 280,
        TABLET_GAP: 16,
    },
    CONTAINER: {
        PADDING_TABLET: 48,
        PADDING_DESKTOP: 96,
    },
    LIMITS: {
        MAX_CARDS_PER_ROW: 6,
        MIN_CARDS_PER_ROW: 1,
    },
    /** Default calling code for India (phone fields, API normalisation) */
    INDIA_COUNTRY_CODE: 91,
    /** Local subscriber length when country is India (without country code) */
    INDIAN_LOCAL_PHONE_DIGIT_COUNT: 10,
    /** Minimum digit length for non-India signatory phone validation */
    NON_INDIA_PHONE_MIN_DIGITS: 6,
    /** Minimum trimmed length for authorised contact display name */
    MIN_AUTHORISED_CONTACT_NAME_LENGTH: 2,
    /** Fixed authorised-contact row keys (matches `AuthorisedFixedRowKey`) */
    AUTHORISED_ROW_KEYS: {
        KDM: 'kdm',
        PRINCIPAL: 'principal',
    },
    /** View Contract wireframe: authorised contacts + signatory row */
    CONTRACT_AUTHORISED: {
        CARD_TITLE: 'Authorised contacts',
        NEEDS_ATTENTION: 'Complete required details',
        ALL_SET: 'All set',
        OTP_HINT: "The signatory's email and phone receive the OTP for signing.",
        WILL_SIGN_TEXT: 'Will sign',
        MAKE_SIGNATORY: 'Make signatory',
        MAKE_SIGNATORY_OTP_TITLE:
            'Set this person as the signatory. They will receive the OTP and sign the contract.',
        WILL_SIGN_OTP_TITLE: 'This person will receive the OTP and sign the contract.',
        SAME_PERSON_AS: 'Same person as',
        SAME_PERSON_PICKER_TITLE: 'Pick a person',
        SAME_PERSON_DONT_LINK: "Don't link",
        EMAIL_CONSENT_WARNING:
            'Email consent is required on file for this contact to receive the OTP. Update details or contact support.',
        MOBILE_CONSENT_WARNING:
            'Mobile consent is required on file for this contact to receive the OTP. Update details or contact support.',
        FIXED_KDM_FALLBACK_LABEL: 'KDM (Key Decision Maker)',
        FIXED_PRINCIPAL_LABEL: 'Principal',
        SAME_AS_NONE: 'Not linked',
        UNLINK_SAME_AS: 'Unlink',
        DELETE_CUSTOM_ROLE: 'Remove role',
        CUSTOM_ROLE_FALLBACK_LABEL: 'Custom role',
        ROLE_TITLE_LABEL: 'Role title',
        ROLE_TITLE_PLACEHOLDER: 'e.g. Vice Principal',
        FULL_NAME: 'Full name',
        EMAIL: 'Email',
        PHONE: 'Phone',
        INVALID_EMAIL: 'Please enter a valid email address.',
        INVALID_PHONE: 'Please enter a valid 10-digit mobile number.',
        ADD_ANOTHER_ROLE: 'Add another role',
    },
    /** View Proposal stakeholder rows (editorial layout) */
    VIEW_PROPOSAL_ROWS: {
        TITLE: 'Stakeholders from your school',
        SUBTITLE: 'These contacts receive the signed contract and any updates.',
        SIGNS_ON_BEHALF: 'Signs on behalf of the school',
        RECEIVES_COPY: 'Receives a copy',
        COPY_EMAIL: 'Copy email',
        COPY_PHONE: 'Copy phone',
    },
    COPY_FEEDBACK_MS: 1500,
    RESIZE_RECALC_DELAY_MS: 100,
    UNKNOWN_INITIAL: '?',
} as const

export const PRODUCT_BLOCK = {
    LOADING_MESSAGE: 'Loading product details...',
    EXPAND_ICON: 'expand_more',
    COLLAPSE_ICON: 'expand_less',
    SUBJECT_TRUNCATE_LENGTH: 20,
    SUBJECT_DISPLAY_LENGTH: 17,
    ELLIPSIS: '...',
    TOGGLE_ARIA_LABEL_PREFIX: 'Toggle product details for ',
    GRADE_WISE_STUDENT_COUNT: 'Grade-wise student count',
    GRADE_WISE_COLUMN_SUBJECT: 'Subject',
    GRADE_WISE_COLUMN_TOTAL: 'Total',
    GRADE_WISE_EMPTY_CELL: '—',
    /** Grade column axis starts at this class number */
    GRADE_WISE_MIN_DISPLAY_GRADE: 1,
    /** Minimum grade columns to show in the matrix (padding when data has fewer grades) */
    GRADE_WISE_MIN_COLUMN_COUNT: 8,
} as const

export const VIEW_PROPOSAL_RM = {
    HELP_TEXT_SUFFIX: '· here to help with this proposal',
    FALLBACK_SUBTITLE: 'Your relationship manager · here to help with this proposal',
    ASK_QUESTION: 'Ask a Question',
    COMPANY_SUFFIX: '· Educational Initiatives',
    FALLBACK_NAME: 'Your relationship manager',
    FALLBACK_INITIALS: 'EI',
    EMAIL_LABEL: 'Email',
    PHONE_LABEL: 'Phone',
    REQUEST_CALLBACK: 'Request a call back',
    OFFLINE_COPY: 'Offline copy',
    DOWNLOAD_PROPOSAL: 'Download proposal as PDF',
    DOWNLOADING: 'Downloading…',
    DOWNLOADED: 'Downloaded',
    DOWNLOAD_HINT: 'Useful for offline review or in-person discussions.',
    DOWNLOAD_ERROR: 'Unable to download the proposal PDF. Please try again.',
    DOWNLOAD_NOT_READY:
        'Proposal PDF is not available yet. It may still be generating — please try again shortly.',
    DRAWER_CLOSE_ANIMATION_MS: 220,
    COPY_FEEDBACK_MS: 1500,
    DOWNLOAD_FEEDBACK_MS: 2000,
    CONTACT_ARIA_PREFIX: 'Contact ',
    ARIA_LABELS: {
        CLOSE: 'Close',
        COPY_PHONE: 'Copy phone',
        COPY_EMAIL: 'Copy email',
    },
} as const

export const VIEW_PROPOSAL_LAYOUT = {
    CONTENT_MAX_WIDTH: 1100,
    CARD_BORDER_RADIUS: 16,
    STAKEHOLDER_CARD_RADIUS: 14,
    PAGE_BACKGROUND: '#f8fafc',
} as const

export const RM_AVATAR_CONSTANTS = {
    IMAGE_SRC: 'assets/images/rm-avatar.png',
    BACKGROUND_COLOR: '#dce8fb',
    CARD_SIZE: 52,
    DRAWER_SIZE: 80,
} as const

export const OFFER_BLOCK = {
    PRODUCT_COLORS: {
        mindspark: '#0e9289',
        asset: '#f58b40',
        cares: '#8675fc',
    },
    DEFAULT_PRODUCT_COLOR: '#9da5ad',
    MIN_PRODUCTS_FOR_TABS: 2,
    PRODUCT_SCROLL_MARGIN_TOP: 32,
    PRODUCT_ANCHOR_ID_PREFIX: 'ob-product-',
    SCROLL_BEHAVIOR: 'smooth' as ScrollBehavior,
    SCROLL_BLOCK: 'start' as ScrollLogicalPosition,
    CURRENCY_SPLIT: {
        CRORE_THRESHOLD: 1e7,
        LAKH_THRESHOLD: 1e5,
        THOUSAND_DIVISOR: 1e3,
        UNIT_CRORE: 'Cr',
        UNIT_LAKH: 'L',
        UNIT_THOUSAND: 'K',
        CRORE_FRACTION_DIGITS: 2,
        LAKH_FRACTION_DIGITS: 2,
        THOUSAND_FRACTION_DIGITS: 0,
    },
    LABELS: {
        WHAT_OFFERED: "What you're being offered",
        AY_PREFIX: 'AY',
        TOTAL_LABEL: 'Total contract value',
        TOTAL_INCLUSIVE_GST: 'inclusive of GST',
        BREAKDOWN_SHOW: 'View detailed breakdown',
        BREAKDOWN_HIDE: 'Hide detailed breakdown',
        BREAKDOWN_COLLAPSE: 'Hide breakdown',
        SUBTOTAL: 'Subtotal',
        GST_18: 'GST 18%',
        GST_EXEMPT: 'GST',
        GST_RATE: 'GST @ 18%',
        GST_EXEMPT_DISPLAY: 'Exempt',
        GRAND_TOTAL: 'Grand total',
        PRODUCT_TOTAL_SUFFIX: 'total',
        INSTALLMENT_SUFFIX: 'quarterly instalments',
        INSTALLMENT_FIRST_PREFIX: 'with the first instalment of',
        PRODUCT_SINGULAR: 'product',
        PRODUCT_PLURAL: 'products',
        COL_SUBJECT: 'Subject',
        COL_GRADE: 'Grade',
        COL_STUDENTS: 'Students',
        COL_UNIT_PRICE: 'Unit price',
        COL_AMOUNT: 'Amount',
    },
    ARIA_LABELS: {
        BREAKDOWN_REGION: 'Detailed pricing breakdown',
        PRODUCT_JUMP_NAV: 'Jump to product',
    },
    GRID_TEMPLATE_COLUMNS:
        'minmax(140px, 2fr) minmax(80px, 1.1fr) minmax(90px, 1.1fr) minmax(120px, 1.4fr) minmax(120px, 1.5fr)',
} as const

export const TRUST_SECTION = {
    PRODUCTS_SUBSCRIBED_LABEL: 'Products Subscribed',
    GLOBE_STATE_MACHINE: 'State Machine 1',
    PAGER_PREVIOUS: 'Previous testimonial',
    PAGER_NEXT: 'Next testimonial',
    PAGER_GO_TO_PREFIX: 'Go to testimonial ',
    GLOBE_CANVAS_WIDTH: 1000,
    GLOBE_CANVAS_HEIGHT: 1000,
} as const

export const VIEW_PROPOSAL_PAGE = {
    PROCESSING_MESSAGE: 'Processing your request...',
} as const

export const EXPIRY_PILL_DATE_FORMAT = 'dd MMM yyyy'

export const KEYBOARD_KEYS = {
    ENTER: 'Enter',
    SPACE: ' ',
} as const

export const PRODUCT_COUNT_LABELS = {
    SINGULAR: 'Product',
    PLURAL: 'Products',
} as const

export const FOOTER_CONSTANTS = {
    SCROLL_PROGRESS_THRESHOLD: 0.5,
    SCROLL_PROGRESS_FULL: 1,
    PROPOSAL_EXPIRES_TEXT: 'Proposal expires on',
    PROPOSAL_EXPIRED_TEXT: 'Proposal expired on',
    ENABLE_ACTIONS_MESSAGE:
        'Please open all product blocks and scroll through each one to enable actions',
    MESSAGES: {
        CONTRACT_READY: 'Contract is ready for review and approval',
        REVIEW_ACCEPT_MESSAGE: 'Review and accept before the expiry date.',
        PROPOSAL_ACCEPTED: 'Proposal accepted',
        PROPOSAL_ACCEPTED_SUBTEXT: 'Our team will reach out to finalise the contract.',
        PROPOSAL_EXPIRED_SUBTEXT: 'Reach out to your relationship manager for a revised quote.',
        PROPOSAL_EXPIRED_BUTTON: 'Proposal Expired',
    },
    BUTTONS: {
        VIEW_CONTRACT: 'View Contract',
        REQUEST_CALL_BACK: 'Request Call Back',
        ACCEPT_PROPOSAL: 'Accept Proposal',
        BACK_TO_ORDERS: 'Back to Orders',
    },
    ICONS: {
        DESCRIPTION: 'description',
        PHONE: 'phone',
        CHECK: 'check',
        ARROW_BACK: 'arrow_back',
    },
    TOOLTIP: {
        POSITION: 'above',
    },
} as const

// Component Loading Messages
export const COMPONENT_LOADING_MESSAGES = {
    ATTACHMENTS: 'Loading attachments...',
    PRODUCT_OVERVIEW: 'Loading product overview...',
    PRICING_DETAILS: 'Loading pricing details...',
} as const

// Product Block Constants
export const PRODUCT_BLOCK_CONSTANTS = {
    LOADING_MESSAGE: 'Loading product information...',
    EXPAND_LABEL: 'Expand',
    COLLAPSE_LABEL: 'Collapse',
    NO_DATA_MESSAGE: 'No product data available',
    ERROR_MESSAGE: 'Error loading product data',
    EMPTY_STRING: '',
    PRODUCT_INDEX_TEMPLATE: 'Product {index} of {total}',
    PRODUCT_VALUE_LABEL: 'Product Value',
    EXPANDED_DETAILS_TEXT: 'Product Details',
    EXPANDED_DETAILS_DESCRIPTION: 'View detailed information about this product',
} as const

// View Proposal specific constants
export const VIEW_PROPOSAL_CONSTANTS = {
    DEFAULT_PROPOSAL_TITLE: 'Order Details',
    PRIMARY_CONTACT_ROLE: 'Primary Contact',
    DEFAULT_EMAIL: '-',
    DEFAULT_PHONE: '-',
    EMPTY_DISPLAY: '-',
    FOOTER_ADDITIONAL_INFO:
        'Please review and accept the proposal before the expiry date. After that, discounts will no longer be applicable.',
    DEFAULT_CURRENCY: 'INR',
    INSTALLMENT_ID_1: '1',
    INSTALLMENT_ID_2: '2',
    CONTACT_ID_1: '1',
    DEFAULT_ORDER_DATES: '28 Aug 2025 - 15 Dec 2025',
    ATTACHMENTS_TITLE: 'Attachments',
    SUBJECT_SEPARATOR: ', ',
    CLASS_SEPARATOR: ', ',
    DESCRIPTION_SEPARATOR: ' - ',
    DESCRIPTION_JOINER: ', ',
    SYSTEM_USER: 'System',
    CONTACT_TITLE: 'Point of Contact',
    CONTACT_DESCRIPTION: 'Primary contact information',
    INSTALLMENT_PREFIX: 'Installment ',
    INSTALLMENTS_TITLE: 'Payment Schedule',
    INSTALLMENTS_DESCRIPTION: 'Installment payment schedule for your order',
} as const

export const SKELETON_CONSTANTS = {
    SHOW_BACK: true,
    TITLE_WIDTH_XL: 'xl',
    ORDER_ID_WIDTH_S: 's',
    ACTION_COUNT: 2,
    BADGE_COUNT: 2,
    AMOUNT_WIDTH_XL: 'xl',
    PRODUCT_SKELETON_COUNT: 3,
    SHOW_HEADER: true,
    HEADER_WIDTH_L: 'l',
    SHOW_SUBHEADER: false,
    LINES_COUNT: 2,
    LINE_WIDTHS: ['m', 's'] as const,
    SHOW_ACTIONS: true,
    ACTIONS_COUNT: 1,
    INSTALLMENT_ITEMS: 3,
    ITEM_HEIGHT: 60,
    FOOTER_COUNT: 2,
    BUTTON_WIDTH: 160,
    BUTTON_HEIGHT: 40,
    GAP: 16,
} as const

export const DIALOG_CONSTANTS = {
    REQUEST_CALLBACK: {
        title: 'Request Call Back',
        message:
            'Are you sure you want to request a call back for this proposal? Our team will contact you shortly.',
        confirmText: 'Request Call Back',
        cancelText: 'Cancel',
        showCommentInput: true,
        commentPlaceholder: 'Add any additional comments or questions...',
        commentLabel: 'Comments (optional)',
        maxLength: 150,
        minLength: 60,
        minLengthMessage: 'Comments must be at least 60 characters when provided.',
        WIDTH: '480px',
    },
    ACCEPT_PROPOSAL: {
        title: 'Accept Proposal',
        message: 'Are you sure you want to accept this proposal? This action cannot be undone.',
        confirmText: 'Accept Proposal',
        cancelText: 'Cancel',
        WIDTH: '400px',
    },
    PROPOSAL_ACCEPTED: {
        TITLE: 'Proposal accepted',
        BODY_PREFIX: 'Thanks for confirming the proposal for',
        BODY_SUFFIX: 'Our team will reach out shortly to finalise the contract.',
        BACK_TO_ORDERS: 'Back to Orders',
        FALLBACK_SCHOOL_NAME: 'your school',
        WIDTH: '480px',
        ACTION_BACK: 'back',
    },
    CALLBACK_SUBMITTED: {
        TITLE: 'Request submitted',
        SUBTEXT: 'Our team will contact you shortly.',
        DONE: 'Done',
        WIDTH: '420px',
        ACTION_DONE: 'done',
    },
    ACTION_ERROR: {
        CLOSE: 'Close',
        WIDTH: '420px',
        ACTION_CLOSE: 'close',
    },
    /** Handoff EiModal shell — 20px radius, light backdrop, content-owned padding */
    PROPOSAL_DIALOG_SHELL: {
        PANEL_CLASS: 'proposal-dialog-panel',
        BACKDROP_CLASS: 'proposal-dialog-backdrop',
        MAX_WIDTH: '92vw',
    },
    SUCCESS: {
        WIDTH: '400px',
        confirmText: 'OK',
        BUTTONS: {
            VIEW_CONTRACT: 'View Contract',
            VIEW_PROPOSAL: 'View Proposal',
            CLOSE: 'Close',
            BACK_TO_ORDERS: 'Back to Orders',
        },
        THEMES: {
            SUCCESS: 'success',
        },
        ACTIONS: {
            PRIMARY: 'primary',
        },
    },
} as const

export const API_CONSTANTS = {
    HTTP_STATUS: {
        NOT_FOUND: 404,
    },
    ORDER_STATUS: {
        UNDER_REVIEW: 'proposal_school_under_reviewed',
        APPROVED: 'proposal_school_approved',
    },
    PAYMENT_MODE: {
        ADVANCE: 'advance',
    },
} as const

export const MESSAGES = {
    CALLBACK_SUCCESS:
        'Your call back request has been submitted successfully. Our team will contact you within 24 hours.',
    CALLBACK_SUCCESS_WITH_COMMENT:
        'Your call back request has been submitted successfully with your comments. Our team will contact you within 24 hours.',
    PROPOSAL_ACCEPTED:
        'Your proposal has been accepted successfully. You will receive a confirmation email shortly.',
    ERROR_CALLBACK: 'Failed to submit callback request. Please try again.',
    ERROR_ACCEPT: 'Failed to accept proposal. Please try again.',
    CALLBACK_STATUS_UPDATE_PARTIAL_SUCCESS: 'Status updated but failed to save comment',
    CALLBACK_STATUS_UPDATE_FAILED: 'Failed to update proposal status',
    PROPOSAL_ACCEPT_FAILED: 'Failed to accept proposal',
    PROPOSAL_DATA_LOAD_ERROR: 'Error loading proposal data',
} as const

export const COMPONENT_CONSTANTS = {
    SKELETON_PRODUCT_ARRAY: [1, 2, 3],
    SKELETON_LINE_WIDTHS: ['m', 's'] as ['m', 's'],
    CALLBACK_REQUESTED_TITLE: 'Call Back Requested',
    PROPOSAL_ACCEPTED_TITLE: 'Proposal Accepted',
    ERROR_TITLE: 'Error',
    VIEW_PROPOSAL_ROUTE: '/view-proposal',
    INITIALIZATION_DELAY: 100,
} as const
